import React, { useEffect, useRef, useState } from 'react';

/**
 * Phase 3A-2: Live cursor presence.
 *
 * Broadcasts the local mouse position (normalised to the overlay's bounding
 * rect, throttled to ~40ms) via Daily app-message 'wh-cursor'. Renders the
 * other participant's cursor as a coloured SVG arrow with a name label.
 *
 * Acceptance:
 *  - No flicker: incoming cursors are kept alive for 4s after the last ping.
 *  - Tutor cursor = red (#E11D48). Student cursor = teal (#007A7A).
 *  - Disabled (renders nothing) when settings.cursors_enabled === false.
 */
const TUTOR_COLOR = '#E11D48';
const STUDENT_COLOR = '#007A7A';

const CursorOverlay = ({ callObject, role, yourName, counterpartName, enabled = true }) => {
  const containerRef = useRef(null);
  const lastSentRef = useRef(0);
  const [remote, setRemote] = useState(null);  // {x, y, name, color, ts}

  // Receive remote cursors
  useEffect(() => {
    if (!callObject || !enabled) return;
    const handler = (e) => {
      const m = e?.data;
      if (!m || m.kind !== 'wh-cursor') return;
      if (m.role === role) return;  // ignore our own echo
      setRemote({
        x: Math.max(0, Math.min(1, m.x ?? 0)),
        y: Math.max(0, Math.min(1, m.y ?? 0)),
        name: m.from || counterpartName || (m.role === 'tutor' ? 'Tutor' : 'Student'),
        color: m.role === 'tutor' ? TUTOR_COLOR : STUDENT_COLOR,
        ts: Date.now(),
      });
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject, role, counterpartName, enabled]);

  // Auto-fade the remote cursor if no ping for 4s
  useEffect(() => {
    if (!remote) return;
    const id = setInterval(() => {
      setRemote((r) => (r && Date.now() - r.ts > 4000 ? null : r));
    }, 1000);
    return () => clearInterval(id);
  }, [remote]);

  // Broadcast local cursor (throttled)
  useEffect(() => {
    if (!callObject || !enabled) return;
    const el = containerRef.current;
    if (!el) return;
    const onMove = (ev) => {
      const now = Date.now();
      if (now - lastSentRef.current < 40) return;
      lastSentRef.current = now;
      const rect = el.getBoundingClientRect();
      const x = (ev.clientX - rect.left) / rect.width;
      const y = (ev.clientY - rect.top) / rect.height;
      if (x < -0.05 || x > 1.05 || y < -0.05 || y > 1.05) return;
      try {
        callObject.sendAppMessage({
          kind: 'wh-cursor',
          x, y, role, from: yourName,
        });
      } catch (_e) {}
    };
    const onLeave = () => {
      try { callObject.sendAppMessage({ kind: 'wh-cursor', x: -1, y: -1, role, from: yourName }); } catch (_e) {}
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, [callObject, role, yourName, enabled]);

  if (!enabled) return <div ref={containerRef} className="absolute inset-0 pointer-events-none" data-testid="cursor-overlay-disabled" />;

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none z-40" data-testid="cursor-overlay">
      {remote && remote.x >= 0 && remote.y >= 0 && (
        <div
          className="absolute transition-transform duration-100 ease-out"
          style={{ left: `${remote.x * 100}%`, top: `${remote.y * 100}%`, transform: 'translate(-2px, -2px)' }}
          data-testid="remote-cursor"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.25))' }}>
            <path d="M3 2 L21 12 L13 13 L11 22 Z" fill={remote.color} stroke="white" strokeWidth="1" />
          </svg>
          <div
            className="absolute left-5 top-3 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white whitespace-nowrap"
            style={{ backgroundColor: remote.color }}
          >
            {remote.name}
          </div>
        </div>
      )}
    </div>
  );
};

export default CursorOverlay;
